// src/components/mpi/D4LeadershipDrillDown.js
import { useEffect, useState, useRef } from 'react';
import { mentoringApi as axios } from '../../axiosInstance';
import { ensureMentoringSession } from '../../lib/session';

function AnimatedBar({ value, color, delay = 0 }) {
  const [width, setWidth] = useState(0);
  useEffect(() => {
    const id = setTimeout(() => setWidth(value ?? 0), delay + 60);
    return () => clearTimeout(id);
  }, [value, delay]);
  return (
    <div style={{ flex: 1, height: 6, borderRadius: 3, background: 'rgba(255,255,255,0.07)', overflow: 'hidden' }}>
      <div style={{
        height: '100%', borderRadius: 3,
        width: `${width}%`,
        background: color,
        boxShadow: `0 0 8px ${color}66`,
        transition: 'width 0.6s cubic-bezier(0.4,0,0.2,1)',
      }} />
    </div>
  );
}

function scoreColor(v) {
  if (v < 50) return '#EF4444';
  if (v < 70) return '#F59E0B';
  return '#10B981';
}

// D3 retention: high score = low risk = good (inverted)
// >= 60 gruen (wenig Risiko), 30-59 gelb, < 30 rot (hohes Risiko)
function retentionColor(score) {
  if (score >= 60) return '#10B981';
  if (score >= 30) return '#F59E0B';
  return '#EF4444';
}

function retentionLabel(score) {
  if (score >= 60) return 'LOW';
  if (score >= 30) return 'MEDIUM';
  return 'HIGH';
}

export default function D4LeadershipDrillDown({ current, previous, scoresRaw, period: initialPeriod, tenant, onClose }) {
  // scoresRaw = full API response (has .d4 at top level)
  // current   = scoresRes.data.current (flat scores like d2_rampup, d3_retention)
  const [scores, setScores] = useState(current ?? null);
  const [prevScores, setPrevScores] = useState(previous ?? null);
  const [d4Data, setD4Data] = useState(scoresRaw?.d4 ?? null);
  const [loading, setLoading] = useState(!current);
  const [error, setError] = useState('');
  const boxRef = useRef(null);

  // Survey response rate
  const [surveyData, setSurveyData] = useState(null);
  const [reminding, setReminding] = useState(false);
  const [remindResult, setRemindResult] = useState(null);

  useEffect(() => {
    let alive = true;
    const tq = tenant ? `?tenant=${encodeURIComponent(tenant)}` : '';
    axios.get(`/mpi/survey-status${tq}`, { withCredentials: true })
      .then(r => { if (alive) setSurveyData(r.data ?? null); })
      .catch(() => {});
    return () => { alive = false; };
  }, [tenant]);

  async function handleRemind() {
    if (reminding) return;
    setReminding(true);
    setRemindResult(null);
    try {
      const res = await axios.post('/mpi/survey-remind', {}, { withCredentials: true });
      const d = res.data;
      setRemindResult(d.dry_run
        ? `DRY_RUN: ${d.sent_count} würden versendet`
        : `${d.sent_count} Erinnerung${d.sent_count !== 1 ? 'en' : ''} versendet`);
    } catch {
      setRemindResult('Fehler beim Versenden');
    } finally {
      setReminding(false);
    }
  }

  // If scores not passed as props, fetch from API
  useEffect(() => {
    if (current) return;
    let alive = true;
    (async () => {
      try {
        await ensureMentoringSession();
        const tq = tenant ? `?tenant=${encodeURIComponent(tenant)}` : '';
        const { data } = await axios.get(`/mpi/scores${tq}`, { withCredentials: true });
        if (!alive) return;
        setScores(data?.current ?? data);
        setPrevScores(data?.previous ?? null);
        setD4Data(data?.d4 ?? null);
      } catch {
        if (alive) setError('Leadership-Daten konnten nicht geladen werden.');
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [current, tenant]);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  function handleOverlayClick(e) {
    if (boxRef.current && !boxRef.current.contains(e.target)) onClose();
  }

  // ── Data extraction ──
  // d4Data comes from top-level API response (not inside current)
  const d4 = d4Data ?? {};
  const d4Score = d4.score != null ? parseFloat(d4.score) : null;
  const responseCount = d4.response_count ?? 0;
  const minResponsesMet = d4.min_responses_met !== false; // default true if field missing
  // Bug B: Survey-only-NL (kein Mentor-Panel). Backend liefert mode:'survey' /
  // mentor_panel:false (auf survey-status bzw. d4). Mentoring-NLs: mentor_panel:true.
  const isSurveyMode = surveyData?.mode === 'survey' || surveyData?.mentor_panel === false
    || d4?.mode === 'survey' || d4?.mentor_panel === false;
  const subscores = d4.subscores ?? {};
  const leadership = subscores.leadership != null ? parseFloat(subscores.leadership) : null;
  const communication = subscores.communication != null ? parseFloat(subscores.communication) : null;
  const empowerment = subscores.empowerment != null ? parseFloat(subscores.empowerment) : null;

  // Trend (previous d4_leadership is flat in previous scores)
  const prevD4Score = prevScores?.d4_leadership != null ? parseFloat(prevScores.d4_leadership) : null;
  const d4Delta = d4Score != null && prevD4Score != null ? d4Score - prevD4Score : null;

  // Team context — field names match API: d2_rampup, d3_retention, d3_risk
  const d2Score = scores?.d2_rampup != null ? parseFloat(scores.d2_rampup)
    : (scores?.d2_0_score != null ? parseFloat(scores.d2_0_score) : null);
  const d2FromSurvey = scores?.d2_rampup == null && d2Score != null;
  const d3Retention = scores?.d3_retention != null ? parseFloat(scores.d3_retention) : null;
  const d3Risk = scores?.d3_risk != null ? parseFloat(scores.d3_risk) : null;
  const d3Score = d3Retention != null && d3Risk != null
    ? (d3Retention + d3Risk) / 2
    : d3Retention ?? d3Risk ?? null;

  const SUBSCORE_CARDS = [
    { key: 'leadership', label: 'Fuehrungskultur', value: leadership, subtitle: 'Feedback, Entwicklung, Fairness', questions: 'Fragen 1-3' },
    { key: 'communication', label: 'Kommunikation', value: communication, subtitle: 'Offenheit & Transparenz', questions: 'Fragen 4-5' },
    { key: 'empowerment', label: 'Empowerment', value: empowerment, subtitle: 'Autonomie & Sinnstiftung', questions: 'Fragen 7-8' },
  ];

  return (
    <div
      onClick={handleOverlayClick}
      style={{
        position: 'fixed', inset: 0,
        background: 'rgba(0,0,0,0.72)',
        backdropFilter: 'blur(8px)',
        zIndex: 9999,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: 20,
      }}
    >
      <div
        ref={boxRef}
        onClick={e => e.stopPropagation()}
        style={{
          width: '100%', maxWidth: 860,
          maxHeight: '85vh',
          background: 'linear-gradient(160deg, #111827 0%, #080b12 100%)',
          border: '1px solid rgba(59,130,246,0.2)',
          borderRadius: 20,
          display: 'flex', flexDirection: 'column',
          boxShadow: '0 32px 80px rgba(0,0,0,0.8), 0 0 0 1px rgba(255,255,255,0.04)',
          overflow: 'hidden',
        }}
      >
        {/* ── Header ── */}
        <div style={{
          padding: '20px 24px 16px',
          borderBottom: '1px solid rgba(255,255,255,0.06)',
          display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between',
          flexShrink: 0,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <div style={{
              width: 8, height: 8, borderRadius: '50%',
              background: '#3b82f6', boxShadow: '0 0 10px #3b82f6',
              flexShrink: 0, marginTop: 5,
            }} />
            <div>
              <div style={{ fontSize: 18, fontWeight: 700, color: 'white', letterSpacing: '-0.3px' }}>
                D4 · Leadership Impact – Drill-Down
              </div>
              <div style={{ marginTop: 5, fontSize: 10, color: 'rgba(255,255,255,0.35)' }}>
                {initialPeriod && <>{initialPeriod} · </>}Anonyme Kulturumfrage
              </div>
            </div>
          </div>
          <button
            onClick={onClose}
            style={{
              background: 'rgba(255,255,255,0.07)',
              border: '1px solid rgba(255,255,255,0.1)',
              borderRadius: 8, color: 'rgba(255,255,255,0.45)',
              cursor: 'pointer', padding: '6px 12px', fontSize: 11, flexShrink: 0,
            }}
          >
            ✕ Schliessen
          </button>
        </div>

        {/* ── Body (scrollable) ── */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '20px 24px' }}>
          {loading && (
            <div style={{ padding: 40, textAlign: 'center', fontSize: 12, color: 'rgba(255,255,255,0.28)', letterSpacing: '0.18em' }}>
              LADE LEADERSHIP-DATEN …
            </div>
          )}
          {error && (
            <div style={{ padding: 40, textAlign: 'center', fontSize: 12, color: '#ef4444' }}>{error}</div>
          )}

          {/* ── Survey Rücklauf (compact) ── */}
          {!loading && surveyData && surveyData.invited_count > 0 && (() => {
            const sv = surveyData;
            const pct = sv.response_rate ?? Math.round((sv.response_count / sv.invited_count) * 100);
            const barColor = pct >= 70 ? '#10B981' : pct >= 50 ? '#F59E0B' : '#EF4444';
            const urgent = sv.days_remaining != null && sv.days_remaining <= 3;
            return (
              <div style={{
                display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap',
                marginBottom: 16, padding: '10px 16px',
                background: 'rgba(255,255,255,0.03)',
                border: '1px solid rgba(255,255,255,0.06)',
                borderRadius: 10,
                fontSize: 11, color: 'rgba(255,255,255,0.55)',
              }}>
                <span style={{ whiteSpace: 'nowrap' }}>
                  Rücklauf: <span style={{ color: barColor, fontWeight: 700 }}>{sv.response_count}</span> von {sv.invited_count} ({pct}%)
                </span>
                <div style={{ width: 120, height: 4, borderRadius: 2, background: 'rgba(255,255,255,0.08)', flexShrink: 0 }}>
                  <div style={{ height: '100%', width: `${Math.min(pct, 100)}%`, borderRadius: 2, background: barColor, transition: 'width 0.4s' }} />
                </div>
                {sv.deadline && (
                  <span style={{ color: urgent ? '#EF4444' : 'rgba(255,255,255,0.4)', fontWeight: urgent ? 600 : 400 }}>
                    {sv.days_remaining != null && sv.days_remaining > 0 ? `Noch ${sv.days_remaining} Tage` : 'Abgelaufen'}
                  </span>
                )}
                {sv.is_open && (
                  <button
                    onClick={handleRemind}
                    disabled={reminding}
                    style={{
                      marginLeft: 'auto',
                      padding: '4px 12px',
                      background: reminding ? '#374151' : '#27AE60',
                      border: 'none', borderRadius: 6,
                      color: '#fff', fontSize: 10, fontWeight: 700,
                      cursor: reminding ? 'not-allowed' : 'pointer',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {reminding ? 'Sendet…' : 'Erinnerung senden'}
                  </button>
                )}
                {remindResult && (
                  <span style={{ fontSize: 10, color: remindResult.startsWith('Fehler') ? '#EF4444' : '#10B981' }}>
                    {remindResult}
                  </span>
                )}
              </div>
            );
          })()}
          {!loading && !isSurveyMode && surveyData && surveyData.invited_count === 0 && d4Score == null && (
            <div style={{
              marginBottom: 16, padding: '10px 16px',
              background: 'rgba(255,255,255,0.03)',
              border: '1px solid rgba(255,255,255,0.06)',
              borderRadius: 10,
              fontSize: 11, color: 'rgba(255,255,255,0.35)',
            }}>
              Keine berechtigten Teilnehmer für diese Periode
            </div>
          )}

          {!loading && !error && (
            <>
              {/* ── Block 1: D4-Gesamtscore + Anonymitaets-Handling ── */}
              {!minResponsesMet ? (
                <div style={{
                  marginBottom: 24, padding: '20px',
                  background: 'rgba(255,255,255,0.03)',
                  border: '1px solid rgba(255,255,255,0.07)',
                  borderRadius: 14,
                  textAlign: 'center',
                }}>
                  <div style={{ fontSize: 28, color: '#475569', marginBottom: 12 }}>—</div>
                  <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.45)', lineHeight: 1.7 }}>
                    {responseCount} Antworten eingegangen — mind. 5 fuer Auswertung benoetigt
                  </div>
                </div>
              ) : (
                <div style={{
                  marginBottom: 24, padding: '20px',
                  background: 'rgba(255,255,255,0.03)',
                  border: '1px solid rgba(255,255,255,0.07)',
                  borderRadius: 14,
                }}>
                  <div style={{ display: 'flex', alignItems: 'flex-end', gap: 16, marginBottom: 16 }}>
                    {d4Score != null && (
                      <>
                        <div>
                          <div style={{
                            fontSize: 48, fontWeight: 800, letterSpacing: '-2px', lineHeight: 1,
                            color: scoreColor(d4Score),
                            fontFamily: "'SF Pro Display', -apple-system, sans-serif",
                          }}>
                            {d4Score.toFixed(0)}
                          </div>
                          <div style={{ fontSize: 9, color: 'rgba(255,255,255,0.3)', marginTop: 4 }}>
                            D4 Gesamtscore
                          </div>
                        </div>
                        {d4Delta != null && (
                          <div style={{
                            display: 'flex', alignItems: 'center', gap: 4,
                            paddingBottom: 8,
                            fontSize: 12, fontWeight: 700,
                            color: d4Delta > 0 ? '#10B981' : d4Delta < 0 ? '#EF4444' : 'rgba(255,255,255,0.3)',
                          }}>
                            <span style={{ fontSize: 10 }}>
                              {d4Delta > 0 ? '▲' : d4Delta < 0 ? '▼' : '→'}
                            </span>
                            {d4Delta > 0 ? '+' : ''}{d4Delta.toFixed(1)} Pkt
                          </div>
                        )}
                      </>
                    )}
                  </div>

                  {/* Response-Count Badge */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <div style={{
                      padding: '3px 10px', borderRadius: 20, fontSize: 10, fontWeight: 600,
                      background: 'rgba(59,130,246,0.1)', border: '1px solid rgba(59,130,246,0.25)',
                      color: '#60a5fa',
                    }}>
                      {responseCount} Antworten eingegangen{['9100', '9102'].includes(String(tenant)) ? ' (modelliert)' : ''}
                    </div>
                    <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.25)' }}>
                      Basiert auf anonymer Kulturumfrage
                    </div>
                  </div>
                </div>
              )}

              {/* ── Block 2: Drei Subscore-Kacheln (3-Spalten-Grid) ── */}
              {minResponsesMet && (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4" style={{ marginBottom: 24 }}>
                  {SUBSCORE_CARDS.map((card, i) => {
                    const color = card.value != null ? scoreColor(card.value) : '#475569';
                    return (
                      <div key={card.key} className="bg-white/10 border border-white/10 rounded-xl p-4">
                        <div style={{ fontSize: 9, color: 'rgba(255,255,255,0.28)', letterSpacing: '0.15em', textTransform: 'uppercase', marginBottom: 10 }}>
                          {card.label}
                        </div>
                        <div style={{
                          fontSize: 36, fontWeight: 800, letterSpacing: '-1.5px', lineHeight: 1,
                          color,
                          fontFamily: "'SF Pro Display', -apple-system, sans-serif",
                          marginBottom: 8,
                        }}>
                          {card.value != null ? card.value.toFixed(0) : '—'}
                        </div>
                        {card.value != null && (
                          <div style={{ marginBottom: 8 }}>
                            <AnimatedBar value={card.value} color={color} delay={i * 80} />
                          </div>
                        )}
                        <div style={{ fontSize: 9, color: 'rgba(255,255,255,0.25)' }}>
                          {card.questions}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              {/* ── Block 3: Team-Kontext-Panel ── */}
              {(d2Score != null || d3Score != null) && (
                <>
                  <div style={{ borderTop: '1px solid rgba(255,255,255,0.06)', marginBottom: 16 }} />
                  <div className="text-white/50 text-sm uppercase tracking-widest" style={{ marginBottom: 12 }}>
                    Team-Signale dieser Niederlassung
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4" style={{ marginBottom: 24 }}>
                    {/* D3 Retention-Risiko */}
                    <div className="bg-white/5 border border-white/10 rounded-xl p-4">
                      <div style={{ fontSize: 9, color: 'rgba(255,255,255,0.28)', letterSpacing: '0.15em', textTransform: 'uppercase', marginBottom: 10 }}>
                        Retention-Risiko
                      </div>
                      {d3Score != null ? (
                        <>
                          <div style={{
                            fontSize: 20, fontWeight: 800, letterSpacing: '-0.5px',
                            color: retentionColor(d3Score),
                            marginBottom: 4,
                          }}>
                            {retentionLabel(d3Score)}
                          </div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                            <AnimatedBar value={d3Score} color={retentionColor(d3Score)} delay={0} />
                            <div style={{ fontSize: 11, color: retentionColor(d3Score), fontWeight: 600, minWidth: 28, textAlign: 'right' }}>
                              {d3Score.toFixed(0)}
                            </div>
                          </div>
                        </>
                      ) : (
                        <div style={{ fontSize: 20, color: '#475569' }}>—</div>
                      )}
                    </div>

                    {/* D2 Ramp-Up Score */}
                    <div className="bg-white/5 border border-white/10 rounded-xl p-4">
                      <div style={{ fontSize: 9, color: 'rgba(255,255,255,0.28)', letterSpacing: '0.15em', textTransform: 'uppercase', marginBottom: 10 }}>
                        {d2FromSurvey ? 'Lernkultur-Score' : 'Ramp-Up Score'}
                      </div>
                      {d2Score != null ? (
                        <>
                          <div style={{
                            fontSize: 20, fontWeight: 800, letterSpacing: '-0.5px',
                            color: scoreColor(d2Score),
                            marginBottom: 4,
                          }}>
                            {d2Score.toFixed(0)}
                          </div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                            <AnimatedBar value={d2Score} color={scoreColor(d2Score)} delay={80} />
                          </div>
                        </>
                      ) : (
                        <div style={{ fontSize: 20, color: '#475569' }}>—</div>
                      )}
                    </div>
                  </div>
                </>
              )}

              {/* ── Fusszeile ── */}
              <div style={{
                padding: '10px 14px',
                background: 'rgba(255,255,255,0.02)',
                borderRadius: 9, borderLeft: '3px solid #3b82f6',
                fontSize: 11, color: 'rgba(255,255,255,0.3)', lineHeight: 1.6,
              }}>
                Basiert auf anonymer Kulturumfrage · {responseCount} Antworten · {initialPeriod}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
